import { Button, Flex, RoleGuard } from 'portal-commons';
import React, { useState } from 'react';
import { faArrowProgress } from '@fortawesome/pro-regular-svg-icons';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import RemoveIcon from '@material-ui/icons/Remove';

import Divider from './Divider';
import InfoItem from './InfoItem';
import InfoItemLayout from './InfoItemLayout';
import RcsSectionBlock from './RcsSectionBlock';
import StatusIndicator from './StatusIndicator';
import CustomizableConfirmationDialog from '../CustomizableConfirmationDialog';
import { NO_RESELLER_ID } from '../../constants';
import { RcsCampaign } from '../../types';
import { CspCampaignSharing, Reseller, campaignController } from '@/apis/core';
import electCnpIcon from '@/assets/images/elect-cnp-icon.svg';
import { S3_ASSETS_PATH } from '@/constants';
import Permissions from '@/constants/permissions';

interface OtherResponsiblePartiesSectionProps {
  campaign: RcsCampaign;
  nominationInfo?: CspCampaignSharing;
  electedDcas: number[];
  reseller?: Reseller;
  onElectCnpClick: () => void;
  onResellerChange?: (resellerUid: string) => void;
}

enum ModalType {
  None = 'none',
  RemoveReseller = 'removeReseller',
}

const OtherResponsiblePartiesSection: React.FC<
  OtherResponsiblePartiesSectionProps
> = ({
  campaign,
  nominationInfo,
  electedDcas,
  reseller,
  onElectCnpClick,
  onResellerChange,
}) => {
  const [modalType, setModalType] = useState(ModalType.None);
  const [resellerUpdating, setResellerUpdating] = useState(false);
  const showElectButton =
    campaign.status !== 'EXPIRED' &&
    (!nominationInfo?.status || nominationInfo.status === 'DECLINED');

  const electCnpRoles = [
    ...new Set([
      ...Permissions.campaignDetail.electCnp.roles,
      ...Permissions.rcsCampaignDetail.electCnp.roles,
    ]),
  ];

  const upstreamCnp = (() => {
    if (!nominationInfo) return 'NONE';
    if (nominationInfo.status === 'DECLINED') {
      return '--';
    }
    return (
      nominationInfo.upstreamCnpName || nominationInfo.upstreamCnpUid || 'NONE'
    );
  })();

  const handleRemoveResellerModalAction = async (submission: boolean) => {
    if (submission) {
      setResellerUpdating(true);
      const response = await campaignController.updateCampaign(campaign.uid, {
        resellerUid: NO_RESELLER_ID,
      });
      if (response) {
        setResellerUpdating(false);
        setModalType(ModalType.None);
        onResellerChange?.(NO_RESELLER_ID);
      }
    } else {
      setModalType(ModalType.None);
    }
  };

  return (
    <RcsSectionBlock
      icon={faArrowProgress}
      title="Other Responsible Parties"
      showDivider={true}
      editable={false}
    >
      <InfoItemLayout.Horizontal
        sx={{
          mb: '20px',
          alignItems: showElectButton ? 'center' : undefined,
        }}
      >
        <InfoItem
          title="Connectivity Partner"
          sx={{ alignItems: showElectButton ? 'center' : undefined }}
        >
          <Flex
            sx={{
              alignItems: 'center',
              gap: 's',
            }}
          >
            {upstreamCnp}
            {showElectButton && (
              <RoleGuard roles={electCnpRoles}>
                <Button
                  data-testid="elect-cnp-btn"
                  color="secondary"
                  shape="square"
                  size="small"
                  style={{ position: 'relative' }}
                  onClick={onElectCnpClick}
                >
                  <img src={electCnpIcon} />
                  <span>Elect CNP</span>
                  <NotificationsNoneIcon
                    style={{
                      position: 'absolute',
                      background: '#D41C62',
                      borderRadius: '50%',
                      right: '-10px',
                      top: '-10px',
                      fontSize: '18px',
                      transform: 'rotate(30deg)',
                      color: '#fff',
                      padding: '2px',
                    }}
                  />
                </Button>
              </RoleGuard>
            )}
          </Flex>
        </InfoItem>
        <InfoItem>
          <StatusIndicator variant={electedDcas.length ? 'success' : 'error'} />
          DCA Election {electedDcas.length ? 'Completed' : 'Pending'}
        </InfoItem>
      </InfoItemLayout.Horizontal>
      <Divider />
      <InfoItemLayout.Vertical sx={{ gap: 0 }}>
        <InfoItemLayout.Horizontal>
          <InfoItem
            sx={{ alignItems: 'center', padding: '8px', marginTop: '-8px' }}
            title="Reseller Involved"
            titleSx={{ color: 't.blue700', fontWeight: 'semiBold' }}
          >
            <Flex
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 'xxl',
              }}
            >
              <Flex
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 'xxs',
                  fontWeight: 500,
                }}
              >
                <img
                  src={`${S3_ASSETS_PATH}/images/${
                    reseller ? 'checked-new' : 'negative-checked'
                  }.svg`}
                />
                <span style={{ color: reseller ? '#00797C' : 'D41C54' }}>
                  {reseller
                    ? reseller.uid === NO_RESELLER_ID
                      ? 'No Reseller'
                      : 'Yes'
                    : 'N/A'}
                </span>
              </Flex>
              {reseller && reseller.uid !== NO_RESELLER_ID && (
                <Button
                  shape="square"
                  color="secondary"
                  size="small"
                  disabled={campaign.status !== 'ACTIVE'}
                  onClick={() => setModalType(ModalType.RemoveReseller)}
                >
                  <RemoveIcon />
                  Remove Reseller
                </Button>
              )}
            </Flex>
          </InfoItem>
        </InfoItemLayout.Horizontal>
        {reseller && reseller.uid !== NO_RESELLER_ID && (
          <>
            <InfoItemLayout.Horizontal>
              <InfoItem
                sx={{ alignItems: 'center', padding: '8px' }}
                title="Reseller Legal Name"
              >
                {reseller.companyName}
              </InfoItem>
              <InfoItem
                sx={{ alignItems: 'center', padding: '8px' }}
                title="Phone Number"
              >
                {reseller.phone}
              </InfoItem>
            </InfoItemLayout.Horizontal>
            <InfoItemLayout.Horizontal>
              <InfoItem
                sx={{ alignItems: 'center', padding: '8px' }}
                title="Reseller ID"
              >
                {reseller.uid}
              </InfoItem>
              <InfoItem
                sx={{ alignItems: 'center', padding: '8px' }}
                title="Email Address"
              >
                {reseller.email}
              </InfoItem>
            </InfoItemLayout.Horizontal>
          </>
        )}
      </InfoItemLayout.Vertical>
      <CustomizableConfirmationDialog
        open={modalType === ModalType.RemoveReseller}
        content={`Please confirm that you are changing the reseller on this campaign to No Reseller.<br /><br /><b>Note:</b> Once No Reseller is specified, it cannot be changed again.`}
        submitText={'Proceed'}
        handleClick={handleRemoveResellerModalAction}
        loader={resellerUpdating}
      />
    </RcsSectionBlock>
  );
};

export default OtherResponsiblePartiesSection;
